import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchFaseDeletability,
  deleteProjectFase,
  type FaseDeletability,
} from '@/api/projectFases';
import {
  faseArchiveIntroLine,
  faseDeletePlanningLine,
  faseDeleteDatesLine,
  faseDeleteUndatedLine,
  faseDeleteFilesLine,
} from '@/utils/faseDeleteCopy';
import { FaseInterventieCheckbox } from './FaseInterventieCheckbox';

interface FaseModalCommonProps {
  /** Prefilled name: the suggested "Fase N" when creating, the current name when editing. */
  initialName: string;
  /** Create or rename. `interventie` is passed only when it must be written (checked on
   *  create, changed on edit). Rejects with an Error whose message is shown inline. */
  onSubmit: (name: string, interventie?: boolean) => Promise<void>;
  /** The fase head carries the Odoo `Interventie` tag (edit mode prefill). */
  initialInterventie?: boolean;
  onClose: () => void;
}

interface FaseModalCreateProps extends FaseModalCommonProps {
  mode: 'create';
}

interface FaseModalEditProps extends FaseModalCommonProps {
  mode: 'edit';
  projectId: number;
  faseId: number;
  /** Called after the fase is deleted server-side, so the parent can refetch. */
  onDeleted: () => void;
}

export type FaseModalProps = FaseModalCreateProps | FaseModalEditProps;

/**
 * One dialog for both halves of fase management from the dashboard tab bar:
 * create a fase, and rename or delete an existing one.
 *
 * Deliberately a modal rather than a hover popover or a `⋯` menu on the tab:
 * the dashboard is used on tablets, where hover does not exist.
 *
 * Delete is a two-step flow inside this same dialog. Step two asks the server
 * whether the fase may go (`GET …/deletable`) and shows what goes with it; a
 * blocked fase is refused with the server's own Dutch copy naming the real
 * blocker, which this component renders as-is and never paraphrases.
 *
 * Since Igor's 2026-09-08 amendment the delete CASCADES into the fase's
 * planning children — scheduled werf-days, possibly with installateurs on
 * them. That makes the confirm step the only warning a user gets, so it states
 * how many days go and on which dates (copy in utils/faseDeleteCopy.ts).
 *
 * Amendment 3 the same day: the server ARCHIVES the fase rather than unlinking
 * it, so the confirm step says the fase can be restored in Odoo and no longer
 * claims the fase-bestanden go with it. The verb stays "Verwijderen" — that is
 * what archiving is called throughout this product.
 */
export function FaseModal(props: FaseModalProps) {
  const { initialName, onSubmit, onClose, mode, initialInterventie = false } = props;
  const [name, setName] = useState(initialName);
  const [interventie, setInterventie] = useState(initialInterventie);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Delete flow (edit mode only): null = not started, otherwise the confirm step.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletability, setDeletability] = useState<FaseDeletability | null>(null);
  const [deletabilityLoading, setDeletabilityLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const busy = submitting || deleting;
  const isEdit = mode === 'edit';
  const titleId = 'fase-modal-title';

  // Focus + select the prefilled name so it can be overtyped immediately.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Escape closes (only when idle, to avoid abandoning an in-flight write).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setSubmitting(true);
    setError(null);
    try {
      const interventieChange = interventie !== initialInterventie ? interventie : undefined;
      await (interventieChange === undefined ? onSubmit(trimmed) : onSubmit(trimmed, interventieChange));
      // Parent unmounts the modal on success; no local close needed.
    } catch (err) {
      setError(err instanceof Error ? err.message : isEdit ? 'Fase opslaan mislukt' : 'Fase aanmaken mislukt');
      setSubmitting(false);
    }
  };

  // Step one of the delete: ask the server whether this fase may go at all, and
  // WHAT goes with it — the planning days, and what happens to the
  // fase-bestanden — before showing the confirm.
  const startDelete = useCallback(async () => {
    if (!isEdit) return;
    const { projectId, faseId } = props as FaseModalEditProps;
    setConfirmingDelete(true);
    setDeleteError(null);
    setDeletability(null);
    setDeletabilityLoading(true);
    try {
      setDeletability(await fetchFaseDeletability(projectId, faseId));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Fase controleren mislukt');
    } finally {
      setDeletabilityLoading(false);
    }
  }, [isEdit, props]);

  // Step two: the actual delete. The server re-runs the whole assessment, so a
  // fase that acquired a collector or a booked vordering between preflight and
  // confirm still comes back as a 409 — surface that message instead of
  // assuming the preflight verdict still holds.
  const confirmDelete = useCallback(async () => {
    if (!isEdit || deleting) return;
    const { projectId, faseId, onDeleted } = props as FaseModalEditProps;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProjectFase(projectId, faseId);
      onDeleted();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Fase verwijderen mislukt');
      setDeleting(false);
    }
  }, [isEdit, deleting, props]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="w-full max-w-sm rounded-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 id={titleId} className="text-base font-extrabold text-td-navy">
            {confirmingDelete ? 'Fase verwijderen?' : isEdit ? 'Fase bewerken' : 'Nieuwe fase toevoegen'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Sluiten"
            className="w-8 h-8 rounded-full flex items-center justify-center text-td-muted hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {confirmingDelete ? (
          <>
            <div className="p-5 space-y-3">
              {deletabilityLoading && <p className="text-sm text-td-muted">Fase controleren…</p>}

              {/* Refused: render the server's blockers verbatim — it names the real
                  reason (collectoren, materiaallijst, vordering, taken). */}
              {deletability && !deletability.deletable && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-td-navy">
                    Deze fase kan niet verwijderd worden.
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    {deletability.blockers.map((b) => (
                      <li key={b.code} className="text-sm text-red-600">{b.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {deletability?.deletable && (
                <div className="space-y-2">
                  <p className="text-sm text-td-navy">{faseArchiveIntroLine(initialName)}</p>
                  {/* The cascade, stated before the user can confirm it. Emphasised
                      when there is actually planning to lose. */}
                  <p
                    className={
                      deletability.plannedChildren.length > 0
                        ? 'text-sm font-semibold text-red-600'
                        : 'text-sm text-td-muted'
                    }
                  >
                    {faseDeletePlanningLine(deletability.plannedChildren)}
                  </p>
                  {faseDeleteDatesLine(deletability.plannedChildren) && (
                    <p className="text-sm text-td-navy">
                      {faseDeleteDatesLine(deletability.plannedChildren)}
                    </p>
                  )}
                  {faseDeleteUndatedLine(deletability.plannedChildren) && (
                    <p className="text-sm text-td-muted">
                      {faseDeleteUndatedLine(deletability.plannedChildren)}
                    </p>
                  )}
                  <p className="text-sm text-td-muted">{faseDeleteFilesLine(deletability.fileCount)}</p>
                </div>
              )}

              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
            </div>

            <div className="flex justify-end gap-2 p-5 pt-0">
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-td-navy hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Terug
              </button>
              {/* Only offered when the server said the fase is empty. */}
              {deletability?.deletable && (
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {deleting && (
                    <span
                      className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  Verwijderen
                </button>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-5 space-y-3">
              <label htmlFor="fase-modal-name" className="block text-sm font-semibold text-td-navy">
                Naam fase
              </label>
              <input
                id="fase-modal-name"
                ref={inputRef}
                type="text"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-td-navy focus:outline-none focus:ring-2 focus:ring-td-blue disabled:bg-gray-50"
                placeholder="Bijv. Fase 2"
              />
              <FaseInterventieCheckbox checked={interventie} onChange={setInterventie} disabled={busy} />
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex items-center justify-between gap-2 p-5 pt-0">
              {/* Delete lives bottom-left, away from the confirm button, and never
                  removes anything on a single tap — it opens the confirm step. */}
              {isEdit ? (
                <button
                  type="button"
                  onClick={startDelete}
                  disabled={busy}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  Verwijderen
                </button>
              ) : (
                <span />
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-td-navy hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={busy || name.trim().length === 0}
                  className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {submitting && (
                    <span
                      className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  {isEdit ? 'Opslaan' : 'Toevoegen'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
